<template>
  <div class="header">
    <div class="header-left-item">
      <div
        v-if="createdAt"
        class="header-last-created-at-label"
      >
        {{ `${i18n.t('transcription.lastCreatedAt')}：` }}
      </div>
      <div>{{ formatDateTimeWithoutSecond(createdAt) }}</div>
    </div>
    <div class="header-right-item">
      <BBtn
        v-if="isRefetchButtonVisible"
        size="mini"
        padding-size="small"
        @click="handleRefetchClick"
      >
        <BIconText
          icon="replay"
        >
          {{ i18n.t('general.update.text') }}
        </BIconText>
      </BBtn>
      <BTooltip
        top
        :content="recreateButtonTooltipMessage"
        :disabled="!recreateButtonTooltipMessage"
      >
        <BBtn
          v-if="isRecreationButtonVisible"
          size="mini"
          padding-size="small"
          class="recreate-btn"
          :disabled="!canRecreate"
          @click="handleRecreateClick"
        >
          <BIconText
            icon="source_notes"
            outlined
          >
            {{ i18n.t('general.recreate') }}
          </BIconText>
        </BBtn>
      </BTooltip>
      <BCopyButton
        v-if="copyContent"
        :text="copyContent"
        :title="i18n.t('transcription.summarizationText')"
        is-detailed-name
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDateTimeWithoutSecond } from '@/utils/date-time';

type TProps = {
  createdAt: string | null;
  isRefetchButtonVisible: boolean;
  canRecreate: boolean;
  recreateButtonTooltipMessage?: string;
  content?: string;
};
type TEmits = {
  'click:recreate': [];
  'click:refetch': [];
}
const props = withDefaults(defineProps<TProps>(), {
  recreateButtonTooltipMessage: null,
  enableTimeLink: true,
  content: '',
});
const emit = defineEmits<TEmits>();

const i18n = useI18n();

const isRecreationButtonVisible = computed(() => {
  return props.canRecreate || props.recreateButtonTooltipMessage;
});

const handleRefetchClick = async () => {
  emit('click:refetch');
};

const handleRecreateClick = async () => {
  emit('click:recreate');
};

const copyContent = computed(() => {
  if (!props.content) return '';
  // 「## 要約結果」のセクションを探す（「概要」セクションを除く）
  // NOTE: プロンプトの内容に依存しているため、プロンプト変更時は対応が必要
  const summaryResultRegex = /(?:^|\n)##\s*要約結果\s*\n([\s\S]*?)(?=\n##|$)/;
  const match = props.content.match(summaryResultRegex);

  if (match && match[1]) return match[1].trim();
  return props.content;
});
</script>

<style lang="scss" scoped>
.header {
  color: $textcolor-light;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  min-height: 28px;
  gap: 4px;
}

.header-left-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.header-right-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.recreate-btn {
  :deep(*) {
    color: #DE9402;
  }
  border: 1px solid #DE9402;
}

.header-last-created-at-label {
  flex: 0 0 auto;
}
</style>
